// src/hooks/useBinanceDepthWS.ts

import { useEffect, useMemo, useRef, useState } from "react";

export interface DepthLevel { price: number; qty: number }
export interface WsDepthSnapshot {
  bids: DepthLevel[];
  asks: DepthLevel[];
  spread: number;
  spreadPct: number;
  mid: number;
  bidSum: number;
  askSum: number;
}

export function useBinanceDepthWS(symbolUSDT: string) {
  const [snap, setSnap] = useState<WsDepthSnapshot | null>(null);
  const [hasError, setHasError] = useState<boolean>(false);
  const wsRef = useRef<WebSocket | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!symbolUSDT) return;
    
    // Clear previous data and reset error state when switching symbols
    setSnap(null);
    setHasError(false);
    
    const sym = symbolUSDT.toLowerCase().endsWith('usdt') ? symbolUSDT.toLowerCase() : `${symbolUSDT.toLowerCase()}usdt`;
    // Use data-stream.binance.com like the working ticker
    // depth20 gives us 20 levels on each side (max available)
    const url = `wss://data-stream.binance.com/ws/${sym}@depth20@100ms`;

    console.log(`[OrderBook] Connecting to ${url}`);

    // Set a timeout to detect if token is not available on Binance
    timeoutRef.current = setTimeout(() => {
      setHasError(true);
      console.warn(`[OrderBook] No data received from Binance for symbol: ${sym} - hiding order book card`);
    }, 5000);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log(`[OrderBook] Connected to ${sym} depth stream`);
      };
      
      ws.onmessage = (ev) => {
        try {
          const raw = JSON.parse(ev.data);
          // Handle both wrapped and direct payload like ticker does
          const payload = raw.data || raw;
          
          // Fields: bids, asks (arrays of [price, qty])
          const bidsArr: [string,string][] = payload.bids || [];
          const asksArr: [string,string][] = payload.asks || [];
          
          if (!bidsArr.length && !asksArr.length) {
            console.warn('[OrderBook] Received empty depth update');
            return;
          }
          
          // Clear the loading timeout since we received data
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          
          const bids = bidsArr.map(([p,q]) => ({ price: Number(p), qty: Number(q) }));
          const asks = asksArr.map(([p,q]) => ({ price: Number(p), qty: Number(q) }));
          const bestBid = bids[0]?.price ?? 0;
          const bestAsk = asks[0]?.price ?? 0;
          const spread = bestAsk > 0 && bestBid > 0 ? bestAsk - bestBid : 0;
          const mid = bestAsk > 0 && bestBid > 0 ? (bestAsk + bestBid) / 2 : 0;
          const spreadPct = mid > 0 ? (spread / mid) * 100 : 0;
          const bidSum = bids.reduce((s,b)=> s + b.qty, 0);
          const askSum = asks.reduce((s,a)=> s + a.qty, 0);
          setSnap({ bids, asks, spread, spreadPct, mid, bidSum, askSum });
        } catch (err) {
          console.error('[OrderBook] Error parsing depth frame:', err);
        }
      };
      
      ws.onerror = (err) => {
        console.error('[OrderBook] WebSocket error:', err);
        setHasError(true);
      };
      
      ws.onclose = () => {
        console.log(`[OrderBook] WebSocket closed for ${sym}`);
      };
    } catch (err) {
      console.error('[OrderBook] Failed to create WebSocket:', err);
      setHasError(true);
    }
    
    return () => {
      try { wsRef.current?.close(); } catch { void 0; }
      wsRef.current = null;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [symbolUSDT]);

  const imbalancePct = useMemo(() => {
    if (!snap) return null;
    const total = snap.bidSum + snap.askSum;
    if (total <= 0) return 0;
    return ((snap.bidSum - snap.askSum) / total) * 100;
  }, [snap]);

  return { snapshot: snap, imbalancePct, hasError };
}


